import React, { useEffect, useState } from "react";
import { Card, Col, Row } from "reactstrap";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import { Link, useHistory } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import BgScreenAuth from "../components/Screen";
import TextInputField from "../../../Components/Forms/TextInputField";
import RoundedButton from "../../../Components/Buttons/RoundedButton";
import CheckBox from "../../../Components/Forms/CheckBox";
import Form from "../../../Components/Forms/Form";
import BackendService from "../../../service/BackendService";
import { LoginFormSchema } from "../../../utils/validationSchema";
import { ToastMessage, Position } from "../../../Components/Toast/ToastMessage";
import { ReactComponent as EyeIcon } from "../../../assets/images/icons/eye.svg";
import { ReactComponent as EyeOfIcon } from "../../../assets/images/icons/eye-off.svg";
import KutubeeLogo from "../../../assets/images/kutubee-logo.svg";
import { getSSOAuthUrl } from "../../../constants/sso";
import "../css/auth.css";

const LoginPage = ({ t }) => {
  document.title = t("Signin.pageTitle");

  const [loading, setLoading] = useState(false);
  const [passwordType, setPasswordType] = useState("password");
  const [ssoLoading, setSsoLoading] = useState(false);
  const history = useHistory();
  const backendService = new BackendService();

  useEffect(() => {
    handleSSOCallback();
  }, []);

  const handleSSOCallback = async () => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const error = params.get("error");

    if (error) {
      ToastMessage.error(t("SSO.registrationRequired"), Position.TOP_RIGHT);
      window.history.replaceState({}, document.title, "/login");
      return;
    }

    if (code) {
      setSsoLoading(true);
      try {
        const accessToken = await backendService.signInWithSSO(code);

        if (accessToken) {
          ToastMessage.success(t("SSO.loginSuccess"), Position.TOP_RIGHT);

          window.history.replaceState({}, document.title, "/login");

          setTimeout(() => {
            history.replace("/");
          }, 1000);
        } else {
          ToastMessage.error(t("SSO.tokenExchangeFailed"), Position.TOP_RIGHT);
          window.history.replaceState({}, document.title, "/login");
        }
      } catch (error) {
        console.error("SSO callback error:", error);
        ToastMessage.error(t("SSO.processingError"), Position.TOP_RIGHT);
        window.history.replaceState({}, document.title, "/login");
      } finally {
        setSsoLoading(false);
      }
    }
  };

  const handleShowPassword = () => {
    setPasswordType((prevType) =>
      prevType === "password" ? "text" : "password"
    );
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      await backendService.signIn(data.email, data.password);
      history.push("/");
    } catch (error) {
      ToastMessage.error(t("Signin.formValidation"), Position.TOP_RIGHT);
    } finally {
      setLoading(false);
    }
  };

  const handleSSOLogin = () => {
    window.location.href = getSSOAuthUrl();
  };

  return (
    <BgScreenAuth>
      <Card className="overflow-hidden card-auth">
        <Row className="g-0">
          <Col className="col-wrap-content-signin" lg={12}>
            <div className="form-wrapper-login">
              <div>
                <h5 className="text-primary login-title-box">اهلاً بعودتك!</h5>
              </div>
              <div className="mt-4">
                <Form onSubmit={onSubmit} schemaType={LoginFormSchema}>
                  {({ control }) => {
                    return (
                      <React.Fragment>
                        {/* Email Field */}
                        <TextInputField
                          control={control}
                          name={"email"}
                          placeholder={t("Signin.usernamePlaceholder")}
                          type={"text"}
                        />

                        {/* Password Field */}
                        <TextInputField
                          control={control}
                          name={"password"}
                          placeholder={t("Signin.passwordPlaceholder")}
                          type={passwordType}
                          leftIcon={
                            <button
                              className="btn btn-link position-absolute end-0 top-0"
                              type="button"
                              onClick={handleShowPassword}
                            >
                              {passwordType == "password" ? (
                                <EyeIcon
                                  width={22}
                                  height={22}
                                  fill="#6ac3db"
                                />
                              ) : (
                                <EyeOfIcon
                                  width={22}
                                  height={22}
                                  fill="#6ac3db"
                                />
                              )}
                            </button>
                          }
                        />
                        <div
                          className="d-flex justify-content-between"
                          style={{
                            color: "#6AC3DB",
                          }}
                        >
                          <CheckBox text={t("Signin.remember")} />

                          <div className="float-end">
                            <Link
                              to="/pass-reset"
                              className="text-muted forget-passsword-text"
                            >
                              {t("Signin.forget")}
                            </Link>
                          </div>
                        </div>

                        <RoundedButton
                          loading={loading}
                          className={"mt-4"}
                          text={t("Signin.login")}
                        />
                        {process.env.REACT_APP_SSO_CLIENT_ID && (
                          <>
                            <div className="d-flex align-items-center my-3">
                              <div className="flex-grow-1 border-top"></div>
                              <span className="mx-3 text-muted">
                                {t("Signin.or")}
                              </span>
                              <div className="flex-grow-1 border-top"></div>
                            </div>
                            <button
                              type="button"
                              onClick={handleSSOLogin}
                              disabled={ssoLoading}
                              className="w-100 rounded-pill bg-transparent p-2 fw-medium d-flex align-items-center justify-content-center gap-2 btn-sso-outline"
                            >
                              <img
                                src={KutubeeLogo}
                                alt="Kutubee SSO"
                                className="sso-logo"
                              />
                              {ssoLoading
                                ? t("SSO.processing")
                                : t("Signin.loginWithKutubee")}
                            </button>
                          </>
                        )}
                      </React.Fragment>
                    );
                  }}
                </Form>
                <ToastContainer position="top-center" rtl={true} />
              </div>
            </div>
          </Col>
        </Row>
      </Card>
    </BgScreenAuth>
  );
};

LoginPage.propTypes = {
  t: PropTypes.any,
};

export default withTranslation()(LoginPage);
